import React from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import { TextInput } from "@/components/ui/TextInput";
import { ToggleSwitch } from "@/components/ui/ToggleSwitch";
import { InlineDropdown } from "@/components/ui/InlineDropdown";
import { PreviewSection, LookupField } from "../shared";
import { useEntityEdit } from "@/contexts/EntityEditContext";
import { ApplicationType } from "@/types/db/applications";
import type { ApplicationDataType } from "@/types/db/applications";
import useDateTime from "@/hooks/useDateTime";
import { useTheme } from "@/hooks/useTheme";
import { ThemedText } from "@/components/ThemedText";
import { MaterialIcons } from "@expo/vector-icons";

const US_STATES = [
  { label: "Alabama", value: "AL" },
  { label: "Alaska", value: "AK" },
  { label: "Arizona", value: "AZ" },
  { label: "Arkansas", value: "AR" },
  { label: "California", value: "CA" },
  { label: "Colorado", value: "CO" },
  { label: "Connecticut", value: "CT" },
  { label: "Delaware", value: "DE" },
  { label: "Florida", value: "FL" },
  { label: "Georgia", value: "GA" },
  { label: "Hawaii", value: "HI" },
  { label: "Idaho", value: "ID" },
  { label: "Illinois", value: "IL" },
  { label: "Indiana", value: "IN" },
  { label: "Iowa", value: "IA" },
  { label: "Kansas", value: "KS" },
  { label: "Kentucky", value: "KY" },
  { label: "Louisiana", value: "LA" },
  { label: "Maine", value: "ME" },
  { label: "Maryland", value: "MD" },
  { label: "Massachusetts", value: "MA" },
  { label: "Michigan", value: "MI" },
  { label: "Minnesota", value: "MN" },
  { label: "Mississippi", value: "MS" },
  { label: "Missouri", value: "MO" },
  { label: "Montana", value: "MT" },
  { label: "Nebraska", value: "NE" },
  { label: "Nevada", value: "NV" },
  { label: "New Hampshire", value: "NH" },
  { label: "New Jersey", value: "NJ" },
  { label: "New Mexico", value: "NM" },
  { label: "New York", value: "NY" },
  { label: "North Carolina", value: "NC" },
  { label: "North Dakota", value: "ND" },
  { label: "Ohio", value: "OH" },
  { label: "Oklahoma", value: "OK" },
  { label: "Oregon", value: "OR" },
  { label: "Pennsylvania", value: "PA" },
  { label: "Rhode Island", value: "RI" },
  { label: "South Carolina", value: "SC" },
  { label: "South Dakota", value: "SD" },
  { label: "Tennessee", value: "TN" },
  { label: "Texas", value: "TX" },
  { label: "Utah", value: "UT" },
  { label: "Vermont", value: "VT" },
  { label: "Virginia", value: "VA" },
  { label: "Washington", value: "WA" },
  { label: "West Virginia", value: "WV" },
  { label: "Wisconsin", value: "WI" },
  { label: "Wyoming", value: "WY" },
  { label: "District of Columbia", value: "DC" },
];

const REFERRAL_SOURCES = [
  { label: "Facebook", value: "Facebook" },
  { label: "Google", value: "Google" },
  { label: "Referral", value: "Referral" },
  { label: "Other", value: "Other" },
];

export interface ApplicationOverviewTabProps {
  application: ApplicationType | null;
  loading?: boolean;
}

export function ApplicationOverviewTab({ application, loading = false }: ApplicationOverviewTabProps) {
  const { editing, formData, updateFormData } = useEntityEdit<ApplicationType>();
  const { formatTimestamp } = useDateTime();
  const { colors } = useTheme();

  const displayData = editing ? formData : application;
  const appData = displayData?.meta_data?.application_data;

  const updateAppData = (updates: Partial<ApplicationDataType>) => {
    updateFormData({
      ...formData,
      meta_data: {
        ...formData?.meta_data,
        application_data: { ...formData?.meta_data?.application_data, ...updates } as ApplicationDataType,
      },
    });
  };

  const fullName = appData
    ? [appData.firstName, appData.middleInitial, appData.lastName].filter(Boolean).join(" ")
    : "";

  const fullAddress = appData
    ? [appData.streetLine1, appData.streetLine2, `${appData.city}, ${appData.state} ${appData.zip}`]
        .filter(Boolean)
        .join("\n")
    : "";

  const referralInfo =
    appData?.referralSource === "Referral" || appData?.referralSource === "Other"
      ? `${appData.referralSource}: ${appData.referralName || ""}`
      : appData?.referralSource || "";

  const showReferralName = appData?.referralSource === "Referral" || appData?.referralSource === "Other";

  return (
    <View style={styles.container} pointerEvents={loading ? "none" : "auto"}>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        {/* Application Details — always readonly */}
        <PreviewSection title="Application Details">
          <LookupField
            label="Application ID"
            value={displayData?.id?.slice(0, 8).toUpperCase()}
            placeholder="—"
            readonly
            loading={loading}
          />
          <LookupField
            label="Applied"
            value={displayData?.created_at ? formatTimestamp(displayData.created_at) : undefined}
            placeholder="—"
            readonly
            loading={loading}
          />
        </PreviewSection>

        {/* Personal Information */}
        <PreviewSection title="Personal Information">
          {editing ? (
            <>
              <TextInput
                label="First Name"
                value={appData?.firstName || ""}
                onChangeText={(text) => updateAppData({ firstName: text })}
                editable={editing}
                loading={loading}
              />
              <TextInput
                label="M.I."
                value={appData?.middleInitial || ""}
                onChangeText={(text) => updateAppData({ middleInitial: text.slice(0, 1) })}
                editable={editing}
                loading={loading}
              />
              <TextInput
                label="Last Name"
                value={appData?.lastName || ""}
                onChangeText={(text) => updateAppData({ lastName: text })}
                editable={editing}
                loading={loading}
              />
              <TextInput
                label="Preferred Name"
                value={appData?.preferredName || ""}
                onChangeText={(text) => updateAppData({ preferredName: text })}
                editable={editing}
                loading={loading}
              />
              <TextInput
                label="Email"
                value={appData?.email || ""}
                onChangeText={(text) => updateAppData({ email: text })}
                editable={editing}
                loading={loading}
              />
              <TextInput
                label="Phone"
                value={appData?.phone || ""}
                onChangeText={(text) => updateAppData({ phone: text })}
                editable={editing}
                loading={loading}
              />
            </>
          ) : (
            <>
              <LookupField
                label="Full Name"
                value={fullName}
                placeholder="No name provided"
                readonly
                loading={loading}
              />
              {appData?.preferredName && (
                <LookupField
                  label="Preferred Name"
                  value={appData.preferredName}
                  placeholder="—"
                  readonly
                  loading={loading}
                />
              )}
              <LookupField label="Email" value={appData?.email} placeholder="No email" readonly loading={loading} />
              <LookupField label="Phone" value={appData?.phone} placeholder="No phone" readonly loading={loading} />
            </>
          )}
        </PreviewSection>

        {/* Address */}
        <PreviewSection title="Address">
          {editing ? (
            <>
              <TextInput
                label="Street Line 1"
                value={appData?.streetLine1 || ""}
                onChangeText={(text) => updateAppData({ streetLine1: text })}
                editable={editing}
                loading={loading}
              />
              <TextInput
                label="Street Line 2"
                value={appData?.streetLine2 || ""}
                onChangeText={(text) => updateAppData({ streetLine2: text })}
                editable={editing}
                loading={loading}
              />
              <TextInput
                label="City"
                value={appData?.city || ""}
                onChangeText={(text) => updateAppData({ city: text })}
                editable={editing}
                loading={loading}
              />
              <InlineDropdown
                options={US_STATES}
                value={appData?.state || ""}
                placeholder="State"
                onSelect={(value) => updateAppData({ state: value })}
              />
              <TextInput
                label="ZIP"
                value={appData?.zip || ""}
                onChangeText={(text) => updateAppData({ zip: text.replace(/\D/g, "").slice(0, 5) })}
                editable={editing}
                loading={loading}
              />
            </>
          ) : (
            <LookupField label="Address" value={fullAddress} placeholder="No address" readonly loading={loading} />
          )}
        </PreviewSection>

        {/* Office & Referral */}
        <PreviewSection title="Office & Referral">
          {editing ? (
            <>
              <TextInput
                label="Office"
                value={appData?.office || ""}
                onChangeText={(text) => updateAppData({ office: text })}
                editable={editing}
                loading={loading}
              />
              <InlineDropdown
                options={REFERRAL_SOURCES}
                value={appData?.referralSource || ""}
                placeholder="Referral Source"
                onSelect={(value) => updateAppData({ referralSource: value, referralName: "" })}
              />
              {showReferralName && (
                <TextInput
                  label="Referral Name"
                  value={appData?.referralName || ""}
                  onChangeText={(text) => updateAppData({ referralName: text })}
                  editable={editing}
                  loading={loading}
                />
              )}
            </>
          ) : (
            <>
              <LookupField
                label="Office"
                value={appData?.office || "Not specified"}
                placeholder="—"
                readonly
                loading={loading}
              />
              <LookupField label="Referral Source" value={referralInfo} placeholder="—" readonly loading={loading} />
            </>
          )}
        </PreviewSection>

        {/* Eligibility */}
        <PreviewSection title="Eligibility">
          <View style={[styles.toggleRow, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <View style={{ flex: 1 }}>
              <ThemedText style={styles.toggleLabel}>Authorized to work in U.S.</ThemedText>
            </View>
            {editing ? (
              <ToggleSwitch
                value={appData?.authorizedToWork ?? false}
                onValueChange={(value) => updateAppData({ authorizedToWork: value })}
              />
            ) : (
              <View style={styles.eligibilityRow}>
                <MaterialIcons
                  name={appData?.authorizedToWork ? "check-circle" : "cancel"}
                  size={20}
                  color={appData?.authorizedToWork ? colors.emerald?.text || "#059669" : colors.ruby?.text || "#DC2626"}
                />
                <ThemedText style={{ fontWeight: "500" }}>{appData?.authorizedToWork ? "Yes" : "No"}</ThemedText>
              </View>
            )}
          </View>

          <View style={[styles.toggleRow, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <View style={{ flex: 1 }}>
              <ThemedText style={styles.toggleLabel}>Over 18</ThemedText>
            </View>
            {editing ? (
              <ToggleSwitch
                value={appData?.over18 ?? false}
                onValueChange={(value) => updateAppData({ over18: value })}
              />
            ) : (
              <View style={styles.eligibilityRow}>
                <MaterialIcons
                  name={appData?.over18 ? "check-circle" : "cancel"}
                  size={20}
                  color={appData?.over18 ? colors.emerald?.text || "#059669" : colors.ruby?.text || "#DC2626"}
                />
                <ThemedText style={{ fontWeight: "500" }}>{appData?.over18 ? "Yes" : "No"}</ThemedText>
              </View>
            )}
          </View>

          <View style={[styles.toggleRow, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <View style={{ flex: 1 }}>
              <ThemedText style={styles.toggleLabel}>Previously worked for NEXUS</ThemedText>
            </View>
            {editing ? (
              <ToggleSwitch
                value={appData?.previouslyWorked ?? false}
                onValueChange={(value) => updateAppData({ previouslyWorked: value })}
              />
            ) : (
              <ThemedText style={{ fontWeight: "500" }}>{appData?.previouslyWorked ? "Yes" : "No"}</ThemedText>
            )}
          </View>
        </PreviewSection>

        {/* Physical Requirements — readonly */}
        <PreviewSection title="Physical Requirements">
          {appData?.physicalRequirements?.every(Boolean) ? (
            <View style={[styles.badge, { backgroundColor: colors.emerald?.background || "#D1FAE5" }]}>
              <MaterialIcons name="check-circle" size={16} color={colors.emerald?.text || "#059669"} />
              <ThemedText style={[styles.badgeText, { color: colors.emerald?.text || "#059669" }]}>
                All requirements confirmed
              </ThemedText>
            </View>
          ) : (
            <View style={[styles.badge, { backgroundColor: colors.ruby?.background || "#FEE2E2" }]}>
              <MaterialIcons name="warning" size={16} color={colors.ruby?.text || "#DC2626"} />
              <ThemedText style={[styles.badgeText, { color: colors.ruby?.text || "#DC2626" }]}>
                {appData?.physicalRequirements?.filter(Boolean).length || 0} of 11 confirmed
              </ThemedText>
            </View>
          )}
        </PreviewSection>

        {/* Additional Questions */}
        <PreviewSection title="Additional Questions">
          <View style={[styles.toggleRow, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <View style={{ flex: 1 }}>
              <ThemedText style={styles.toggleLabel}>Heat-related injury history</ThemedText>
            </View>
            {editing ? (
              <ToggleSwitch
                value={appData?.heatInjury ?? false}
                onValueChange={(value) => updateAppData({ heatInjury: value })}
              />
            ) : (
              <ThemedText style={{ fontWeight: "500" }}>
                {appData?.heatInjury === null ? "—" : appData?.heatInjury ? "Yes" : "No"}
              </ThemedText>
            )}
          </View>

          <View style={[styles.toggleRow, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <View style={{ flex: 1 }}>
              <ThemedText style={styles.toggleLabel}>Has transportation</ThemedText>
            </View>
            {editing ? (
              <ToggleSwitch
                value={appData?.hasTransportation ?? false}
                onValueChange={(value) => updateAppData({ hasTransportation: value })}
              />
            ) : (
              <ThemedText style={{ fontWeight: "500" }}>
                {appData?.hasTransportation === null ? "—" : appData?.hasTransportation ? "Yes" : "No"}
              </ThemedText>
            )}
          </View>

          <View style={[styles.toggleRow, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <View style={{ flex: 1 }}>
              <ThemedText style={styles.toggleLabel}>Event services experience</ThemedText>
            </View>
            {editing ? (
              <ToggleSwitch
                value={appData?.eventServicesExperience ?? false}
                onValueChange={(value) => updateAppData({ eventServicesExperience: value })}
              />
            ) : (
              <ThemedText style={{ fontWeight: "500" }}>
                {appData?.eventServicesExperience === null ? "—" : appData?.eventServicesExperience ? "Yes" : "No"}
              </ThemedText>
            )}
          </View>

          <View style={[styles.toggleRow, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <View style={{ flex: 1 }}>
              <ThemedText style={styles.toggleLabel}>Has smartphone</ThemedText>
            </View>
            {editing ? (
              <ToggleSwitch
                value={appData?.hasCellPhone ?? false}
                onValueChange={(value) => updateAppData({ hasCellPhone: value })}
              />
            ) : (
              <ThemedText style={{ fontWeight: "500" }}>
                {appData?.hasCellPhone === null ? "—" : appData?.hasCellPhone ? "Yes" : "No"}
              </ThemedText>
            )}
          </View>

          <TextInput
            label="Additional info"
            value={appData?.additionalInfo || ""}
            onChangeText={(text) => updateAppData({ additionalInfo: text, hasAdditionalInfo: text.length > 0 })}
            multiline
            numberOfLines={3}
            editable={editing}
            loading={loading}
          />
        </PreviewSection>

        {/* Admin Notes */}
        <PreviewSection title="Admin Notes">
          <TextInput
            label="Notes"
            value={displayData?.meta_data?.notes || ""}
            onChangeText={(text) =>
              updateFormData({
                ...formData,
                meta_data: { ...formData?.meta_data, notes: text },
              })
            }
            multiline
            numberOfLines={3}
            editable={editing}
            loading={loading}
          />
        </PreviewSection>

        {/* Submission Details — always readonly */}
        <PreviewSection title="Submission Details">
          <LookupField
            label="Source"
            value={displayData?.meta_data?.source || "web"}
            placeholder="—"
            readonly
            loading={loading}
          />
          {displayData?.meta_data?.submittedAt && (
            <LookupField
              label="Submitted"
              value={formatTimestamp(displayData.meta_data.submittedAt)}
              placeholder="—"
              readonly
              loading={loading}
            />
          )}
          {displayData?.meta_data?.reviewedAt && (
            <LookupField
              label="Reviewed"
              value={formatTimestamp(displayData.meta_data.reviewedAt)}
              placeholder="—"
              readonly
              loading={loading}
            />
          )}
        </PreviewSection>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    gap: 8,
    padding: 16,
  },
  eligibilityRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  badge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 16,
    alignSelf: "flex-start",
  },
  badgeText: {
    fontSize: 13,
    fontWeight: "500",
  },
  toggleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  toggleLabel: {
    fontSize: 14,
    fontWeight: "600",
    marginBottom: 2,
  },
});
